import type { ApiClient } from '../client';

/** Semantic role of a footer link. `terms` / `privacy` replace the static
 *  Terms-of-Service / Privacy anchors across the iframe; `other` is a plain
 *  footer entry (backend default for untyped legacy rows). */
export type FooterLinkType = 'terms' | 'privacy' | 'other';

const FOOTER_LINK_TYPES: readonly FooterLinkType[] = ['terms', 'privacy', 'other'];

/** One partner footer link — configured per operator in the operator
 *  console / admin, rendered in the My Packs / Delivery footers. */
export interface FooterLink {
  label: string;
  url: string;
  type: FooterLinkType;
}

interface RawResponse {
  footer_links?: unknown;
}

function isRawLink(value: unknown): value is { label?: unknown; url?: unknown; type?: unknown } {
  return typeof value === 'object' && value !== null;
}

function normalizeType(value: unknown): FooterLinkType {
  return FOOTER_LINK_TYPES.includes(value as FooterLinkType)
    ? (value as FooterLinkType)
    : 'other';
}

/**
 * Partner footer links for the current session's operator from
 * GET /api/game_platform/iframe/v1/footer_links. Soft-fails: any non-OK /
 * malformed response returns `[]`, which the footer treats the same as
 * "operator configured no links" (footer hidden).
 */
export async function getFooterLinks(client: ApiClient): Promise<FooterLink[]> {
  try {
    const raw = await client.request<RawResponse>(
      '/loot_machine/api/game_platform/iframe/v1/footer_links',
      { method: 'GET' },
    );
    if (!Array.isArray(raw?.footer_links)) return [];
    return raw.footer_links.filter(isRawLink).flatMap((link) => {
      const label = typeof link.label === 'string' ? link.label.trim() : '';
      const url = typeof link.url === 'string' ? link.url.trim() : '';
      return label && url ? [{ label, url, type: normalizeType(link.type) }] : [];
    });
  } catch {
    return [];
  }
}
